import { Suspense } from 'react';
import { Route } from 'react-router-dom';
import { FeatureProtectedRoute } from '@/components/FeatureProtectedRoute';
import { PageLoader } from './PageLoader';
import { lazyWithReload } from '@/lib/lazyWithReload';
import { marcarHito } from '@/lib/analytics/hitosDelArranque';
import type { FeatureName } from '@smarty/shared';

/**
 * Una ruta del panel que pide su CODIGO apenas arranca la navegacion.
 *
 * 🚨 EL DEFECTO ES ESTRUCTURAL Y ALCANZA A 137 RUTAS. `FeatureProtectedRoute` devuelve un spinner
 *    mientras carga los permisos, asi que su `children` —el `Suspense` con la pantalla perezosa— no
 *    se renderiza, y el `import()` de ese chunk NI SIQUIERA ARRANCA hasta que los permisos vuelven.
 *    Medido con los hitos del arranque en las colas: 450 ms esperando el codigo, con los datos ya
 *    en el navegador sin poder mostrarse.
 *
 *    Hoy hay 137 rutas detras de esa puerta y TRES con precarga.
 *
 * ⭐ EL `loader` CORRE ANTES DE RENDERIZAR. Desde aca el chunk se pide al empezar la navegacion, en
 *    paralelo con lo que sea que la pantalla necesite. Es lo que hacen React Router 6.4+
 *    (`route.lazy`) y Remix de fabrica.
 *
 * 🪤 ESTO NO PRECARGA DATOS, Y LA DISTINCION IMPORTA. El codigo de una pantalla se puede pedir sin
 *    saber nada de ella; sus datos, no —hay que conocer la consulta—. Mezclar las dos cosas en un
 *    solo helper obligaria a que cada ruta declare su consulta para poder usarlo, y entonces nadie
 *    lo usaria. Se gana lo generico ahora y lo especifico cuando cada pantalla lo declare
 *    (ver `rutaDeCola`, que hace las dos).
 *
 * 🪤 Y NO TOCA LA PUERTA DE PERMISOS: sigue decidiendo que se MUESTRA. Deja de decidir cuando se
 *    DESCARGA, que es una pregunta distinta y no es suya.
 */

type Modulo = () => Promise<{ default: React.ComponentType }>;

/** Un componente perezoso por modulo, creado UNA vez — no en cada render. */
const cache = new Map<Modulo, ReturnType<typeof lazyWithReload>>();
function pantallaDe(modulo: Modulo) {
  let c = cache.get(modulo);
  if (!c) {
    c = lazyWithReload(modulo);
    cache.set(modulo, c);
  }
  return c;
}

export function rutaDelPanel(
  path: string,
  modulo: Modulo,
  opciones?: {
    /**
     * La feature que la protege. Sin esto, la ruta no pasa por la puerta de permisos.
     *
     * 🪤 Es `FeatureName`, no `string`: una feature inventada no compila. El typecheck lo pidio
     *    apenas se escribio esto, y tenia razon — una ruta protegida por un nombre que no existe
     *    queda abierta sin que nadie lo note.
     */
    feature?: FeatureName;
    /** Datos a precalentar, si la pantalla los declaro. Nunca se espera: `Suspense` ya sabe. */
    precargarDatos?: () => void;
  },
): React.ReactElement {
  const Pantalla = pantallaDe(modulo);
  const contenido = (
    <Suspense fallback={<PageLoader variant="content" />}>
      <Pantalla />
    </Suspense>
  );
  return (
    <Route
      key={path}
      path={path}
      loader={() => {
        /*
         * El codigo primero: es lo que mas tarda en estar listo y no depende de nada.
         *
         * ⭐ Y SE MARCA CUANDO LLEGA. Al vivir en la fabrica, TODAS las rutas reportan este hito sin
         *    que nadie lo cablee — que es la ventaja de tener un solo lugar donde nacen las rutas.
         */
        void modulo().then(() => marcarHito('chunk')).catch(() => {});
        opciones?.precargarDatos?.();
        // 🪤 Se devuelve `null` y NO la promesa: React Router espera el valor del loader, asi que
        //    devolverla congelaria la navegacion en la pantalla anterior.
        return null;
      }}
      element={
        opciones?.feature
          ? <FeatureProtectedRoute feature={opciones.feature}>{contenido}</FeatureProtectedRoute>
          : contenido
      }
    />
  );
}
